import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
    throw new Error('Unauthorized');
  }
  const data = await res.json();
  if (!res.ok && !data.success) throw new Error(data.detail || 'Request failed');
  return data;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api('/api/auth/me')
        .then(d => {
          setUser(d.user);
          // Backfill the user's timezone from the browser whenever the account
          // doesn't have one stored yet. We fire this on EVERY /auth/me load
          // (not just signup) so older accounts get their zone the moment
          // they open the app in a browser that reports one. If the user has
          // manually picked a zone in Settings, we respect it and don't
          // overwrite — they may be travelling.
          if (d.user && !d.user.timezone) {
            try {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              if (tz) {
                api('/api/users/me', { method: 'PUT', body: JSON.stringify({ timezone: tz }) })
                  .then(r => { if (r.user) { setUser(r.user); localStorage.setItem('user', JSON.stringify(r.user)); } })
                  .catch(() => {});
              }
            } catch { /* ignore */ }
          }
        })
        .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Shared helper — PATCH the browser's detected timezone onto the account if
  // the account has none stored. Called from login() AND signup() so the
  // first interactive moment after auth always leaves the server with a zone.
  const backfillTimezoneIfMissing = async (userDoc) => {
    if (!userDoc || userDoc.timezone) return userDoc;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return userDoc;
      const r = await api('/api/users/me', { method: 'PUT', body: JSON.stringify({ timezone: tz }) });
      return r?.user || userDoc;
    } catch {
      return userDoc;
    }
  };

  const login = async (email, password) => {
    const data = await api('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    localStorage.setItem('token', data.token);
    // Backfill timezone immediately if the account doesn't have one — covers
    // every user who signed up before timezone support shipped.
    const enriched = await backfillTimezoneIfMissing(data.user);
    localStorage.setItem('user', JSON.stringify(enriched));
    setUser(enriched);
    return { ...data, user: enriched };
  };

  const signup = async (email, password, full_name) => {
    let timezone = '';
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* ignore */ }
    const data = await api('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name, timezone })
    });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const data = await api('/api/auth/me');
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
